import { Component, Input, OnDestroy } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SubscriptionList } from '@vendasta/rx-utils';
import { WorkStates } from '@vendasta/rx-utils/work-state';
import { MEDDICScores, MEDDICSection, MEDDICSectionData } from '@vendasta/sales-opportunities';
import { EMPTY, Observable, ReplaySubject, combineLatest, firstValueFrom, of } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { MEDDICScoreSource } from '../meddic-scores/m-e-d-d-i-c-score-source';
import { computeProgress } from '../meddic-scores/meddic-scores';
import { MeddicNotesService } from './meddic-notes.service';

@Component({
  selector: 'sales-ui-meddic-section',
  templateUrl: './meddic-section.component.html',
  styleUrls: ['./meddic-section.component.scss'],
})
export class MeddicSectionComponent implements OnDestroy {
  readonly isSending$: Observable<boolean>;
  readonly isFailed$: Observable<boolean>;
  readonly meddicProgress$: Observable<number>;
  readonly scores$: Observable<MEDDICScores>;
  readonly scoresState: WorkStates<MEDDICScores>;
  readonly getSectionsState: WorkStates<MEDDICSectionData[]>;
  notesSavedSuccessfully$: Observable<boolean>;
  notesAreSaving$: Observable<boolean>;
  MEDDIC_METRICS_SECTION = MEDDICSection.MEDDIC_SECTION_METRICS;
  MEDDIC_ECONOMIC_BUYER_SECTION = MEDDICSection.MEDDIC_SECTION_ECONOMIC_BUYER;
  MEDDIC_DECISION_CRITERIA_SECTION = MEDDICSection.MEDDIC_SECTION_DECISION_CRITERIA;
  MEDDIC_DECISION_PROCESS_SECTION = MEDDICSection.MEDDIC_SECTION_DECISION_PROCESS;
  MEDDIC_IDENTIFY_PAIN_SECTION = MEDDICSection.MEDDIC_SECTION_IDENTIFY_PAIN;
  MEDDIC_CHAMPION_SECTION = MEDDICSection.MEDDIC_SECTION_CHAMPION_SCORE;
  MEDDIC_COMPETITION_SECTION = MEDDICSection.MEDDIC_SECTION_COMPETITION;
  MEDDIC_PAPER_PROCESS_SECTION = MEDDICSection.MEDDIC_SECTION_PAPER_PROCESS;
  private readonly opportunityId$$ = new ReplaySubject<string>(1);
  readonly opportunityId$ = this.opportunityId$$.pipe(
    filter((id) => !!id),
    distinctUntilChanged(),
  );
  private readonly fallbackSalespersonId$$ = new ReplaySubject<string>(1);
  readonly fallbackSalespersonId$ = this.fallbackSalespersonId$$.pipe(
    filter((id) => !!id),
    distinctUntilChanged(),
  );
  private readonly accountGroupId$$ = new ReplaySubject<string>(1);
  readonly accountGroupId$ = this.accountGroupId$$.pipe(filter((id) => !!id));
  private readonly subscriptions = SubscriptionList.new();

  constructor(
    private readonly scoreSvc: MEDDICScoreSource,
    private readonly alerts: SnackbarService,
    private readonly notesService: MeddicNotesService,
  ) {
    this.subscriptions.add(
      combineLatest([this.opportunityId$, this.accountGroupId$]),
      ([opportunityId, accountGroupId]) => this.notesService.initializeMeddicSections(accountGroupId, opportunityId),
    );

    this.notesSavedSuccessfully$ = this.opportunityId$.pipe(switchMap((id) => this.scoreSvc.notesSuccessEvent$(id)));

    const noteSaveEvents$ = this.opportunityId$.pipe(switchMap((id) => this.scoreSvc.notesSaveEvents$(id)));

    this.subscriptions.add(noteSaveEvents$, (eventSuccessful) => {
      if (eventSuccessful) {
        this.alerts.openSuccessSnack('MEDDIC.NOTES_SAVED');
      } else {
        this.alerts.openErrorSnack('MEDDIC.PROBLEM_SAVING_NOTES');
      }
    });

    this.notesAreSaving$ = this.opportunityId$.pipe(switchMap((id) => this.scoreSvc.areNotesCurrentlySaving$(id)));
    this.scores$ = combineLatest([this.opportunityId$, this.accountGroupId$]).pipe(
      switchMap(([oppId, accountGroupId]) => {
        return scoreSvc.getCachedScoresOrFetch$(accountGroupId, oppId);
      }),
      publishReplay(1),
      refCount(),
    );
    this.meddicProgress$ = this.scores$.pipe(map((scores) => computeProgress(scores) * 100));
    this.scoresState = {
      isLoading$: this.opportunityId$.pipe(switchMap((id) => scoreSvc.isLoading$(id))),
      isSuccess$: this.opportunityId$.pipe(switchMap((id) => scoreSvc.isLoadSuccess(id))),
      workResults$: this.scores$,
      successEvents$: EMPTY,
    };

    this.getSectionsState = {
      isLoading$: this.opportunityId$.pipe(switchMap((id) => scoreSvc.areSectionsCurrentlyLoading$(id))),
      isSuccess$: this.opportunityId$.pipe(switchMap((id) => scoreSvc.didSectionsLoadSuccessfully(id))),
      workResults$: this.opportunityId$.pipe(switchMap((id) => scoreSvc.getMeddicSections(id))),
      successEvents$: EMPTY,
    };

    this.isSending$ = this.opportunityId$.pipe(
      switchMap((id) => scoreSvc.isSubmitting$(id)),
      switchMap((isSending) => {
        if (isSending) {
          return of(true);
        }
        // More satisfying UX for very fast requests
        return of(false).pipe(delay(350));
      }),
    );
    this.isFailed$ = this.opportunityId$.pipe(switchMap((id) => scoreSvc.isSubmitFailed$(id)));
    const success$ = this.opportunityId$.pipe(switchMap((id) => scoreSvc.submissionSuccessEvents$(id)));
    this.subscriptions.add(success$, (success) => {
      if (success) {
        alerts.openSuccessSnack('MEDDIC scores saved'); // TODO: Translate
      } else {
        alerts.openErrorSnack('Failed to save MEDDIC scores');
      }
    });
  }

  @Input() set accountGroupId(id: string) {
    this.accountGroupId$$.next(id);
  }

  @Input() set opportunityId(id: string) {
    this.opportunityId$$.next(id);
  }

  @Input() set fallbackSalespersonId(id: string) {
    this.fallbackSalespersonId$$.next(id);
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  async saveMEDDICScores(): Promise<void> {
    const [agid, oppId, scores] = await Promise.all([
      firstValueFrom(this.accountGroupId$),
      firstValueFrom(this.opportunityId$),
      firstValueFrom(this.scores$),
    ]);
    this.scoreSvc.submitMEDDICScores(agid, oppId, scores);
  }

  async handleScoresChanged($event: MEDDICScores): Promise<void> {
    const [agid, oppId] = await Promise.all([
      firstValueFrom(this.accountGroupId$),
      firstValueFrom(this.opportunityId$),
    ]);
    this.scoreSvc.submitMEDDICScores(agid, oppId, $event);
  }

  async retryGettingMEDDICSections(): Promise<void> {
    const [oppId, accountGroupId] = await Promise.all([
      firstValueFrom(this.opportunityId$),
      firstValueFrom(this.accountGroupId$),
    ]);
    this.scoreSvc.initializeMeddicSections(accountGroupId, oppId);
  }

  async retrySavingMEDDICNotes(): Promise<void> {
    const oppId = await firstValueFrom(this.opportunityId$);
    this.notesService.retrySavingMEDDICNotes(oppId);
  }
}
